import createDOMPurify from 'dompurify';
import { isTemplatePath } from './template.js';
import { ALLOWED_ATTRIBUTES, ALLOWED_TAGS, isAllowedAssetUrl, } from './rules.js';
const URL_ATTRIBUTES = new Set(['src', 'href']);
const SECTION = /\{\{([#^/])\s*([^{}]*?)\s*\}\}/y;
const TAG_START = /[A-Za-z/!?]/;
/**
 * Turns the section tags in text into comments. The HTML parser moves text out of a table to just
 * before it ("foster parenting"), so `<tbody>{{#rows}}<tr>` would come out with the section emptied
 * and its rows outside it; a comment stays where it was written. Tags inside attributes are text in
 * a value and are left alone.
 */
function protectSections(document, marker) {
    let result = '';
    let inTag = false;
    let quote = '';
    for (let i = 0; i < document.length;) {
        const char = document[i];
        if (inTag) {
            if (quote) {
                if (char === quote)
                    quote = '';
            }
            else if (char === '"' || char === "'")
                quote = char;
            else if (char === '>')
                inTag = false;
        }
        else if (char === '<' && TAG_START.test(document[i + 1] ?? '')) {
            inTag = true;
        }
        else if (char === '{') {
            SECTION.lastIndex = i;
            const match = SECTION.exec(document);
            if (match && isTemplatePath(match[2])) {
                result += `<!--${marker}${match[1]}${match[2]}-->`;
                i += match[0].length;
                continue;
            }
        }
        result += char;
        i += 1;
    }
    return result;
}
function restoreSections(document, marker) {
    return document.replace(new RegExp(`<!--${marker}([#^/])(.*?)-->`, 'g'), '{{$1$2}}');
}
export function createWidgetSanitizer(window) {
    const purify = createDOMPurify(window);
    const droppedUrls = [];
    let marker = '';
    // Dropped as nodes, not from the serialised string, where `<!--` can also sit in an attribute.
    purify.addHook('uponSanitizeElement', (node, data) => {
        if (data.tagName === '#comment' && !node.textContent?.startsWith(marker))
            node.parentNode?.removeChild(node);
    });
    purify.addHook('uponSanitizeAttribute', (_node, data) => {
        if (URL_ATTRIBUTES.has(data.attrName) && !isAllowedAssetUrl(data.attrValue)) {
            data.keepAttr = false;
            droppedUrls.push(data.attrName);
        }
    });
    return (document) => {
        droppedUrls.length = 0;
        // Fresh per call, so a comment the author wrote can never be read back as a section.
        marker = `mf-section-${Math.random().toString(36).slice(2)}:`;
        const clean = purify.sanitize(protectSections(document, marker), {
            ALLOWED_TAGS: [...ALLOWED_TAGS, '#comment'],
            ALLOWED_ATTR: ALLOWED_ATTRIBUTES,
            ALLOW_DATA_ATTR: true,
            ALLOW_ARIA_ATTR: true,
            CUSTOM_ELEMENT_HANDLING: {
                tagNameCheck: /^mf-(chart|filter|action)$/,
                attributeNameCheck: /^(dataset|field|height|name|type)$/,
                allowCustomizedBuiltInElements: false,
            },
        });
        const removed = purify.removed.map((entry) => 'attribute' in entry ? (entry.attribute?.name ?? '') : entry.element.nodeName.toLowerCase());
        return {
            document: restoreSections(clean, marker),
            removed: [...new Set([...removed, ...droppedUrls])].filter((name) => name !== 'body'),
        };
    };
}
